// ============================================================================
// MODAL NOVO CURSO — DASHBOARD ADM
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/ModalNovoCurso.tsx
// UPDATED: 2026-03-10 — versão final consolidada
//
// FEATURES:
//   [1] Afiliado opcional — lista (UUID) ou sigla FIL-2026-XXXX
//   [2] Imagem de capa — URL ou upload de arquivo com preview
//   [3] Link externo do curso
//   [4] Body envia apenas campos reconhecidos pelo CreateCourseDTO
//   [5] Sem "afiliado obrigatório" — admin pode criar sem vínculo
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  X, BookOpen, Loader2, Building2, Image, Link, Upload, AlertTriangle,
} from 'lucide-react';
import { API_BASE, authH } from '../../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface AfiliadoOption {
  id: string;
  nome: string;
  status: string;
}

interface FormData {
  courseName: string;
  description: string;
  category: string;
  instructorName: string;
  price: string;
  durationHours: string;
  modality: 'presencial' | 'online' | 'hibrido';
  affiliateRaw: string;   // UUID ou FIL-2026-XXXX
  externalUrl: string;
  coverImageUrl: string;
}

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const CATEGORIAS = [
  'Cabelo', 'Maquiagem', 'Estética', 'Unhas',
  'Barba', 'Spa', 'Cosmetologia', 'Massoterapia', 'Outro',
];

const INITIAL_FORM: FormData = {
  courseName: '', description: '', category: '',
  instructorName: '', price: '', durationHours: '',
  modality: 'presencial', affiliateRaw: '',
  externalUrl: '', coverImageUrl: '',
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SIGLA_REGEX = /^[A-Z]+-\d{4}-[A-Z0-9]+$/;

// ─── Component ───────────────────────────────────────────────────────────────
const ModalNovoCurso: React.FC<Props> = ({ onClose, onSuccess }) => {

  const [form, setForm]                 = useState<FormData>(INITIAL_FORM);
  const [afiliados, setAfiliados]       = useState<AfiliadoOption[]>([]);
  const [loadingAfil, setLoadingAfil]   = useState(true);
  const [salvando, setSalvando]         = useState(false);
  const [erro, setErro]                 = useState<string | null>(null);

  // Tabs
  const [imagemTab, setImagemTab]       = useState<'url' | 'upload'>('url');
  const [afiliadoTab, setAfiliadoTab]   = useState<'lista' | 'sigla'>('lista');

  // Imagem
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileInputRef                    = useRef<HTMLInputElement>(null);

  // ── Busca todos os afiliados (sem filtro de status) ───────────────────────
  useEffect(() => {
    const load = async () => {
      setLoadingAfil(true);
      try {
        const res = await fetch(`${API_BASE}/affiliates?limit=200`, { headers: authH() });
        if (!res.ok) throw new Error();
        const data = await res.json();
        const raw: any[] = data.affiliates ?? data.results ?? data.data ?? [];
        setAfiliados(
          raw.map(a => ({
            id:     a.id,
            nome:   a.schoolName ?? a.nomeEmpresa ?? a.id,
            status: a.status ?? '',
          }))
        );
      } catch {
        setAfiliados([]);
      } finally {
        setLoadingAfil(false);
      }
    };
    load();
  }, []);

  const set = (field: keyof FormData, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // ── Upload de imagem ──────────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview local imediato
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    // Tenta upload real
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'course_cover');
      const res = await fetch(`${API_BASE}/documents/upload`, {
        method: 'POST',
        headers: {
          Authorization: (authH() as Record<string, string>).Authorization,
        },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url ?? data.fileUrl ?? data.path ?? '';
        if (url) set('coverImageUrl', url);
      }
    } catch { /* preview local fica ativo mesmo sem upload */ }
    finally { setUploadingImg(false); }
  };

  const handleUrlChange = (url: string) => {
    set('coverImageUrl', url);
    setImagePreview(url || null);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    // Validações obrigatórias
    if (!form.courseName.trim())     { setErro('Nome do curso é obrigatório'); return; }
    if (!form.category)              { setErro('Selecione uma categoria'); return; }

   const price         = form.price.trim() === '' ? 0 : parseFloat(form.price);
    const durationHours = parseInt(form.durationHours, 10);
    if (isNaN(price) || price < 0)                 { setErro('Preço inválido'); return; }
    if (isNaN(durationHours) || durationHours < 1) { setErro('Carga horária inválida (mínimo 1h)'); return; }

    // Monta body — apenas campos reconhecidos pelo CreateCourseDTO
   const body: Record<string, any> = {
      courseName:   form.courseName.trim(),
      category:     form.category,
      price:        price, // 0 = gratuito
      durationHours,
      modality:     form.modality,
    };

    if (form.instructorName.trim()) body.instructorName = form.instructorName.trim();
    if (form.description.trim())    body.description    = form.description.trim();
    if (form.externalUrl.trim())    body.externalUrl    = form.externalUrl.trim();
    if (form.coverImageUrl.trim())  body.coverImageUrl  = form.coverImageUrl.trim();

    // Afiliado: UUID → affiliateId | FIL-2026-XXXX → affiliateSystemId | vazio → omite
    const raw = form.affiliateRaw.trim().toUpperCase();
    if (raw) {
      if (UUID_REGEX.test(form.affiliateRaw.trim())) {
        body.affiliateId = form.affiliateRaw.trim();
      } else if (SIGLA_REGEX.test(raw)) {
        body.affiliateSystemId = raw;
      } else {
        setErro('Formato do afiliado inválido. Use UUID ou sigla FIL-2026-XXXX');
        return;
      }
    }

    setSalvando(true);
    try {
      const res = await fetch(`${API_BASE}/courses`, {
        method: 'POST',
        headers: authH(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = Array.isArray(err.message)
          ? err.message.join(' · ')
          : err.message ?? `Erro ${res.status}`;
        throw new Error(msg);
      }

      onSuccess();
      onClose();
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao criar curso. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">

        {/* HEADER */}
        <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900">Novo Curso</h3>
              <p className="text-sm text-gray-500">
                Criado como <span className="font-bold text-amber-600">pendente</span> — requer aprovação para exibir no site
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={salvando}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">

            {/* Nome */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Nome do Curso <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.courseName}
                onChange={e => set('courseName', e.target.value)}
                placeholder="Ex: Colorimetria Capilar Avançada"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold" />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Descrição</label>
              <textarea value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={3} placeholder="Descreva o conteúdo e objetivos do curso..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-gray-900" />
            </div>

            {/* Categoria + Instrutor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Categoria <span className="text-red-500">*</span>
                </label>
                <select value={form.category} onChange={e => set('category', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 font-semibold">
                  <option value="">Selecione...</option>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Instrutor</label>
                <input type="text" value={form.instructorName}
                  onChange={e => set('instructorName', e.target.value)}
                  placeholder="Nome completo do instrutor"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-semibold" />
              </div>
            </div>

            {/* Preço + Carga Horária + Modalidade */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
Preço (R$) <span className="text-xs font-normal text-gray-400">(opcional — deixe em branco para gratuito)</span>                </label>
                <input type="number" min="0" step="0.01" value={form.price}
                  onChange={e => set('price', e.target.value)} placeholder="0.00"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-semibold" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Carga Horária (h) <span className="text-red-500">*</span>
                </label>
                <input type="number" min="1" value={form.durationHours}
                  onChange={e => set('durationHours', e.target.value)} placeholder="Ex: 40"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-semibold" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Modalidade</label>
                <select value={form.modality}
                  onChange={e => set('modality', e.target.value as FormData['modality'])}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 font-semibold">
                  <option value="presencial">Presencial</option>
                  <option value="online">Online</option>
                  <option value="hibrido">Híbrido</option>
                </select>
              </div>
            </div>

            {/* Link Externo */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <Link className="w-4 h-4 text-blue-500" /> Link do Curso
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>
              <input type="url" value={form.externalUrl}
                onChange={e => set('externalUrl', e.target.value)}
                placeholder="https://plataforma.com/curso/xyz"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900" />
            </div>

            {/* Imagem de Capa */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <Image className="w-4 h-4 text-purple-500" /> Imagem de Capa
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>

              {/* Tabs */}
              <div className="flex gap-2 mb-3">
                {(['url', 'upload'] as const).map(tab => (
                  <button key={tab} type="button"
                    onClick={() => setImagemTab(tab)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                      imagemTab === tab
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {tab === 'url' ? '🔗 URL' : '📁 Upload'}
                  </button>
                ))}
              </div>

              {imagemTab === 'url' ? (
                <input type="url" value={form.coverImageUrl}
                  onChange={e => handleUrlChange(e.target.value)}
                  placeholder="https://exemplo.com/imagem-capa.jpg"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-900" />
              ) : (
                <>
                  <input ref={fileInputRef} type="file" accept="image/*"
                    onChange={handleFileChange} className="hidden" />
                  <button type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImg}
                    className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-all flex items-center justify-center gap-2 font-semibold disabled:opacity-50">
                    {uploadingImg
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                      : <><Upload className="w-4 h-4" /> Clique para selecionar imagem</>}
                  </button>
                </>
              )}

              {/* Preview */}
              {imagePreview && (
                <div className="mt-3 relative">
                  <img src={imagePreview} alt="Preview"
                    className="w-full h-36 object-cover rounded-xl border-2 border-purple-200"
                    onError={() => setImagePreview(null)} />
                  <button type="button"
                    onClick={() => { setImagePreview(null); set('coverImageUrl', ''); }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-600">
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Afiliado */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-blue-500" />
                Afiliado Responsável
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>

              {/* Tabs */}
              <div className="flex gap-2 mb-3">
                {(['lista', 'sigla'] as const).map(tab => (
                  <button key={tab} type="button"
                    onClick={() => { setAfiliadoTab(tab); set('affiliateRaw', ''); }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                      afiliadoTab === tab
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {tab === 'lista' ? '📋 Selecionar' : '🏷️ Sigla FIL-2026-XXXX'}
                  </button>
                ))}
              </div>

              {afiliadoTab === 'lista' ? (
                loadingAfil ? (
                  <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Carregando afiliados...
                  </div>
                ) : afiliados.length > 0 ? (
                  <select value={form.affiliateRaw}
                    onChange={e => set('affiliateRaw', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 font-semibold">
                    <option value="">— Nenhum (sem vínculo) —</option>
                    {afiliados.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.nome}
                        {a.status === 'pending'  ? ' ⏳' : ''}
                        {a.status === 'active'   ? ' ✓'  : ''}
                        {a.status === 'inactive' ? ' ○'  : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Nenhum afiliado encontrado. Use a aba{' '}
                      <button type="button" onClick={() => setAfiliadoTab('sigla')}
                        className="font-bold underline hover:text-amber-900">
                        "Sigla FIL-2026-XXXX"
                      </button>{' '}
                      ou deixe sem vínculo.
                    </p>
                  </div>
                )
              ) : (
                <div>
                  <input type="text" value={form.affiliateRaw}
                    onChange={e => set('affiliateRaw', e.target.value.toUpperCase())}
                    placeholder="Ex: FIL-2026-AAJJYW"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 font-mono text-sm uppercase" />
                  <p className="text-xs text-gray-400 mt-1.5">
                    Encontre na página <strong>Filiados</strong> → coluna "ID Sistema"
                  </p>
                </div>
              )}
            </div>

            {/* Aviso de fluxo */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-bold text-blue-800">ℹ️ Fluxo de publicação</p>
              <p className="text-sm text-blue-700 mt-1">
                Criado como <strong>Pendente</strong> → ADM aprova → aparece automaticamente
                em <code className="bg-blue-100 px-1 rounded">/cursos</code>
              </p>
            </div>

            {/* Erro */}
            {erro && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-red-700 font-semibold text-sm">{erro}</p>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200 flex gap-3">
            <button type="button" onClick={onClose} disabled={salvando}
              className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={salvando || uploadingImg}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {salvando
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Criando...</>
                : <><BookOpen className="w-5 h-5" /> Criar Curso</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalNovoCurso;
